/* ============================================
   ROOM DESIGNER — Wall Segment Module
   Asset file: custom-room-designer-wallseg.js

   Handles wall segment / divider line tool:
   click handling, chain finalization, snap logic,
   collision checks, wall-face quantization,
   and segment group rendering (hollow walls, T-junctions).
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

class RoomDesignerWallSeg {
  constructor(rd) {
    this.rd = rd;
    this._installMethods();
  }

  _installMethods() {
    const rd = this.rd;
    // Bind each method onto the RD instance so existing call sites
    // (this.methodName(...)) keep working unchanged.
    rd._resetWallSegmentState = this._resetWallSegmentState.bind(rd);
    rd._syncWallSegmentBtn = this._syncWallSegmentBtn.bind(rd);
    rd._applyPenDrawModeSwitch = this._applyPenDrawModeSwitch.bind(rd);
    rd._warnIncompleteRoom = this._warnIncompleteRoom.bind(rd);
    rd._finishWallSegmentChain = this._finishWallSegmentChain.bind(rd);
    rd._findConnectedGroup = this._findConnectedGroup.bind(rd);
    rd._isChainEndpointOnWall = this._isChainEndpointOnWall.bind(rd);
    rd._isPointOnWallOrDivider = this._isPointOnWallOrDivider.bind(rd);
    rd._isChainClosed = this._isChainClosed.bind(rd);
    rd._isChainCommittable = this._isChainCommittable.bind(rd);
    rd._handleWallSegmentClick = this._handleWallSegmentClick.bind(rd);
    rd._segmentIntersectsObject = this._segmentIntersectsObject.bind(rd);
    rd._segmentCrossesDoor = this._segmentCrossesDoor.bind(rd);
    rd._quantizeWallSnap = this._quantizeWallSnap.bind(rd);
    rd._quantizeWallEdgeSnap = this._quantizeWallEdgeSnap.bind(rd);
    rd._isPointInsideAnyRoom = this._isPointInsideAnyRoom.bind(rd);
    rd._isPointOnAnyDoor = this._isPointOnAnyDoor.bind(rd);
    /* CUSTOM_ROOMDESIGNER_BY_CLAUDE — these two shipped 2026-07-30 with the
       doorway-snap feature and were never bound here, so every `this._findDoorSnap`
       in the click handler threw TypeError. The throw landed in the ONE branch that
       runs when a click snaps to nothing — the free pivot point in the middle of a
       room — which killed the second click of a three-click divider silently. */
    rd._getDoorSnapPoints = this._getDoorSnapPoints.bind(rd);
    rd._findDoorSnap = this._findDoorSnap.bind(rd);
    rd._drawWallSegmentGroups = this._drawWallSegmentGroups.bind(rd);
    rd._undoWallSegment = this._undoWallSegment.bind(rd);
  }

  // ── Methods extracted from custom-room-designer.js ──
  // Each method uses 'this' which is bound to the RD instance via .bind(rd)

  _resetWallSegmentState() {
    // Never commit an unclosed free-standing chain (it divides nothing) — drop it
    if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0
        && this._isChainCommittable(this.currentWallSegmentGroup)) {
      this.wallSegmentGroups.push(this.currentWallSegmentGroup);
    }
    this.currentWallSegmentGroup = null;
    this.currentWall = null;
    this.hasPlacedFirstPoint = false;
    this._wsSnapSticky = null;
    this._wsLineSide = null;
    this._clearCheckPulse();
    this._clearArrowPulse();
  }

  _syncWallSegmentBtn() {
    const hasRoom = this.rooms.some(r => r.closed);
    if (hasRoom === this._lastHasClosedRoom) return;
    this._lastHasClosedRoom = hasRoom;
    const btn = document.getElementById('wallSegmentBtn');
    const lineBtn = document.getElementById('wallLineBtn');
    if (!btn) return;
    const hint = document.getElementById('wallSegmentHint');
    const btns = [btn, lineBtn].filter(Boolean);
    if (hasRoom) {
      for (const b of btns) {
        b.classList.remove('disabled', 'disabled-with-tooltip');
        b.removeAttribute('aria-disabled');
      }
      btn.title = 'Draw interior walls to divide a space into rooms';
      if (lineBtn) lineBtn.title = 'Draw thin dividing lines to create zones';
      if (hint) hint.textContent = 'Partition a space into rooms and zones.';
    } else {
      // If currently in wallSegment mode, kick back to rectangle
      if (this.penDrawMode === 'wallSegment') {
        const rectBtn = document.querySelector('.pen-draw-mode-btn[data-value="rectangle"]');
        if (rectBtn) this._applyPenDrawModeSwitch(rectBtn, 'rectangle');
      }
      const disabledClass = this._isMobile ? 'disabled' : 'disabled-with-tooltip';
      for (const b of btns) {
        b.classList.add(disabledClass);
        b.setAttribute('aria-disabled', 'true');
        b.title = 'Draw a perimeter first to use dividers';
      }
      if (hint) hint.textContent = 'Draw a perimeter first to unlock dividers';
    }
  }

  _applyPenDrawModeSwitch(btn, newMode) {
    // Block wall segment mode if no closed rooms exist
    if (newMode === 'wallSegment' && !this.rooms.some(r => r.closed)) return;
    this._penToolClicked = true;
    this._clearPopoverIdlePulse();
    document.querySelectorAll('.pen-draw-mode-btn').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const eraseBtn = document.querySelector('.pen-erase-mode-btn');
    if (eraseBtn) eraseBtn.classList.remove('active');
    // Finish any in-progress wall segment chain when switching away
    if (this.penDrawMode === 'wallSegment' && newMode !== 'wallSegment') {
      this._finishWallSegmentChain();
    }
    // Also finish when switching between wall and line sub-types
    if (this.penDrawMode === 'wallSegment' && newMode === 'wallSegment') {
      const newSegType = btn.dataset.segType || 'wall';
      if (newSegType !== this._wallSegmentType) this._finishWallSegmentChain();
    }
    this.penDrawMode = newMode;
    if (newMode === 'wallSegment') this._wallSegmentType = btn.dataset.segType || 'wall';
    this._penCancelledIdle = false;
    this._resetRectState(); this._resetFreehandState();
    if (newMode === 'wallSegment') {
      document.getElementById('penActionOptions').style.display = 'none';
      const penBadge = document.getElementById('penBadge');
      if (penBadge) penBadge.style.display = 'none';
    } else {
      document.getElementById('penActionOptions').style.display = '';
    }
    if (this._isMobileToolbar) {
      this.mouseWorld = null;
      this.mouseWorldRaw = null;
      this.currentWall = null;
      this.hasPlacedFirstPoint = false;
      if (this.currentRoom && this.currentRoom.walls.length > 0 && !this.currentRoom.closed) {
        this.currentRoom = { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
      }
      if (newMode !== 'wallSegment') {
        this._mobileActionChosen = false;
        document.querySelectorAll('.pen-mode-btn').forEach(b => {
          if (b.dataset.value !== 'erase') b.classList.remove('active');
        });
      }
      this._updateJoystickVisibility();
      this._updateFloatingCancel();
      this._updateFloatingRepeat();
      this._startPopoverIdlePulse();
      this.render();
      return;
    }
    if (this.activeTool !== 'pen') {
      this.setActiveTool('pen');
    } else if (this.mode !== 'draw') {
      this.mode = 'draw';
      this.canvas.style.cursor = 'crosshair';
      if (newMode !== 'wallSegment') {
        if (!this.currentRoom || this.currentRoom.walls.length === 0 || this.currentRoom.closed) this.promoteUnclosedRoom();
      }
      this.updateStatus(newMode === 'rectangle' ? 'Click two corners to draw a rectangular perimeter. Esc to exit' : newMode === 'wallSegment' ? 'Click to place interior wall points. Enter, Esc, or double-click to finish.' : 'Click to place perimeter points. Esc to exit');
    }
    const showPop = (id) => { const el = document.getElementById(id); if (el) el.classList.add('visible'); };
    showPop('penPopover');
    this._pinnedPopover = 'pen';
    const syncPenButtons = () => {
      if (this._penToolClicked && (this.activeTool === 'pen' || (this.activeTool === 'eraser' && this._isMobileToolbar))) {
        const eraserActive = this.activeTool === 'eraser';
        document.querySelectorAll('.pen-draw-mode-btn').forEach(b => {
          const match = b.dataset.value === this.penDrawMode;
          const segMatch = b.dataset.value !== 'wallSegment' || (b.dataset.segType || 'wall') === this._wallSegmentType;
          b.classList.toggle('active', !eraserActive && !this._penCancelledIdle && match && segMatch);
        });
      }
    };
    syncPenButtons();
    this.render();
  }

  _warnIncompleteRoom(onDiscard, onContinue) {
    // Remove any existing warning
    const existing = this.container.querySelector('.rd-incomplete-warning');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.className = 'rd-incomplete-warning';
    overlay.innerHTML = '<div class="rd-incomplete-warning-card">' +
      '<div class="rd-incomplete-warning-title">Incomplete Room</div>' +
      '<p class="rd-incomplete-warning-msg">Your room shape isn\'t closed yet. Close the shape to create a room, or discard the walls.</p>' +
      '<div class="rd-incomplete-warning-actions">' +
        '<button class="rd-incomplete-warning-btn primary" data-action="continue">Continue Drawing</button>' +
        '<button class="rd-incomplete-warning-btn secondary" data-action="discard">Discard Walls</button>' +
      '</div></div>';
    this.container.appendChild(overlay);
    const cleanup = () => { overlay.remove(); };
    overlay.querySelector('[data-action="continue"]').addEventListener('click', () => { cleanup(); if (onContinue) onContinue(); });
    overlay.querySelector('[data-action="discard"]').addEventListener('click', () => { cleanup(); if (onDiscard) onDiscard(); this.render(); });
    // Click backdrop to continue drawing
    overlay.addEventListener('click', (e) => { if (e.target === overlay) { cleanup(); if (onContinue) onContinue(); } });
  }

  _finishWallSegmentChain() {
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — Esc and Enter KEEP what you've drawn. There
    // used to be a gate here: a chain that neither closed on itself nor reached a
    // wall put up a "Shape Not Closed" dialog offering only Discard or Continue
    // Drawing. Neither is what you want when the free-standing wall IS the thing you
    // meant to draw — a partition you'll hang other walls off later.
    //
    // The gate dated from before free-standing geometry was supported at all; the
    // 2026-07-22 union-find work in computeRoomZones made a floating chain safe.
    // Measured, rather than assumed: a free-standing segment, an L, and a U each
    // give one zone at the full 600 sq ft of the test room, with no zero-area faces
    // and no phantom holes, and a free-standing CLOSED loop still carves its island.
    // A chain that divides nothing simply doesn't change the zoning, which is the
    // right answer — not a reason to refuse to draw it.
    this._wsEscPendingDiscard = false;
    let hiddenLine = false;
    if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) {
      this.saveUndoState();
      const segs = this.currentWallSegmentGroup.segments;
      const doors = this.currentWallSegmentGroup.doors;
      const chainStart = segs[0].start;
      const chainEnd = segs[segs.length - 1].end;

      // Find committed groups of the same type connected at chain start or end
      const chainType = this.currentWallSegmentGroup.type || 'wall';
      const startGroup = this._findConnectedGroup(chainStart, chainType);
      let endGroup = this._findConnectedGroup(chainEnd, chainType);
      if (endGroup && endGroup === startGroup) endGroup = null; // same group = loop, not a bridge

      if (startGroup) {
        for (const s of segs) startGroup.segments.push(s);
        for (const d of doors) startGroup.doors.push(d);
        // Bridge two separate groups into one
        if (endGroup) {
          for (const s of endGroup.segments) startGroup.segments.push(s);
          for (const d of endGroup.doors) startGroup.doors.push(d);
          const idx = this.wallSegmentGroups.indexOf(endGroup);
          if (idx !== -1) this.wallSegmentGroups.splice(idx, 1);
        }
        // Merge collinear segments after combining groups
        const mw = { walls: startGroup.segments };
        this.mergeCollinearWalls(mw);
        startGroup.segments = mw.walls;
      } else if (endGroup) {
        for (const s of segs) endGroup.segments.push(s);
        for (const d of doors) endGroup.doors.push(d);
        const mw = { walls: endGroup.segments };
        this.mergeCollinearWalls(mw);
        endGroup.segments = mw.walls;
      } else {
        this.wallSegmentGroups.push(this.currentWallSegmentGroup);
      }
      hiddenLine = (chainType === 'line' && !this.showSeamLines && (this._floorMode || this._equipmentMode));
    }
    this.currentWallSegmentGroup = null;
    this.currentWall = null;
    this.hasPlacedFirstPoint = false;
    this._wsSnapSticky = null;
    this._wsLineSide = null;
    // Rebuild space cards so zone breakdown updates immediately
    this.updateMeasurements();
    return hiddenLine;
  }

  /** Segment-level undo during active chain drawing.
   *  Returns true if handled (intercepted), false to fall through to normal undo. */
  _undoWallSegment() {
    // Guard: must be in wall segment drawing mode with an active chain
    if (this.activeTool !== 'pen' || this.penDrawMode !== 'wallSegment') return false;
    if (!this.currentWallSegmentGroup) return false;
    const segs = this.currentWallSegmentGroup.segments;

    // No segments placed yet — nothing to undo at chain level
    if (segs.length === 0 && !this.hasPlacedFirstPoint) return false;

    // If first point placed but no segments yet, just cancel the first point
    if (segs.length === 0 && this.hasPlacedFirstPoint) {
      this.currentWallSegmentGroup = null;
      this.currentWall = null;
      this.hasPlacedFirstPoint = false;
      this._wsSnapSticky = null;
      this._wsLineSide = null;
      this._wsEscPendingDiscard = false;
      this.updateStatus('Chain start cancelled. Click a wall to start a new chain.');
      this.render();
      return true;
    }

    this._wsEscPendingDiscard = false;

    // Check if the undo stack top corresponds to this drawing session
    // (its currentWallSegmentGroup has fewer segments than current — confirms it's from this chain)
    const undoTop = this.undoStack.length > 0 ? this.undoStack[this.undoStack.length - 1] : null;
    const undoTopSegs = undoTop && undoTop.currentWallSegmentGroup
      ? undoTop.currentWallSegmentGroup.segments : null;
    const canRestoreFromStack = undoTopSegs && undoTopSegs.length < segs.length;

    // Push current state to redo stack for redo support
    const redoEntry = {
      rooms: JSON.parse(JSON.stringify(this.rooms)),
      currentRoom: this.currentRoom ? JSON.parse(JSON.stringify(this.currentRoom)) : null,
      objects: JSON.parse(JSON.stringify(this.objects)),
      wallSegmentGroups: JSON.parse(JSON.stringify(this.wallSegmentGroups)),
      currentWallSegmentGroup: JSON.parse(JSON.stringify(this.currentWallSegmentGroup)),
      measurements: JSON.parse(JSON.stringify(this._measurements)),
      measureNextId: this._measureNextId,
      _wsChainRestore: true
    };
    this.redoStack.push(redoEntry);

    if (canRestoreFromStack) {
      // Restore from undo stack — handles collinear un-merge correctly
      const state = this.undoStack.pop();
      this.rooms = JSON.parse(JSON.stringify(state.rooms));
      this.objects = JSON.parse(JSON.stringify(state.objects || []));
      this.currentRoom = state.currentRoom ? JSON.parse(JSON.stringify(state.currentRoom)) : { walls: [], doors: [], cutouts: [], type: this.roomType, closed: false };
      this.wallSegmentGroups = state.wallSegmentGroups ? JSON.parse(JSON.stringify(state.wallSegmentGroups)) : [];
      this.currentWallSegmentGroup = state.currentWallSegmentGroup ? JSON.parse(JSON.stringify(state.currentWallSegmentGroup)) : null;
      if (state.measurements) { this._measurements = JSON.parse(JSON.stringify(state.measurements)); this._measureNextId = state.measureNextId || 1; }
      this._relinkDoorWallRefs();
      for (const r of this.rooms) { r._zoneCache = null; r._zoneLayouts = null; }
    } else {
      // No matching undo entry (e.g. collinear merge had no saveUndoState) — manually pop last segment
      segs.pop();
    }

    // Re-establish drawing state from restored/remaining chain
    const restoredSegs = this.currentWallSegmentGroup ? this.currentWallSegmentGroup.segments : [];

    if (restoredSegs.length === 0) {
      // All segments removed — cancel the chain entirely
      this.currentWallSegmentGroup = null;
      this.currentWall = null;
      this.hasPlacedFirstPoint = false;
      this._wsSnapSticky = null;
      this._wsLineSide = null;
      this.updateStatus('Segment removed. Click a wall to start a new chain.');
    } else {
      // Resume drawing from the last segment's endpoint
      const lastSeg = restoredSegs[restoredSegs.length - 1];
      this.currentWall = {
        start: { x: lastSeg.end.x, y: lastSeg.end.y },
        end: { x: lastSeg.end.x, y: lastSeg.end.y }
      };
      this.hasPlacedFirstPoint = true;
      this.updateStatus('Segment removed. Continue drawing or Esc to finish.');
    }

    this.render();
    this.updateMeasurements();
    return true;
  }

  _findConnectedGroup(point, type) {
    for (const group of this.wallSegmentGroups) {
      if (type && (group.type || 'wall') !== type) continue;
      for (const seg of group.segments) {
        if (this.getDistance(point, seg.start) < 0.01 || this.getDistance(point, seg.end) < 0.01) return group;
        const param = this.getWallParam(point, seg);
        const proj = this.paramToPoint(seg, param);
        if (param > 0.01 && param < 0.99 && this.getDistance(point, proj) < 0.01) return group;
      }
    }
    return null;
  }

  /** Is this point anchored to a room wall or a committed divider (endpoint or T)? */
  _isPointOnWallOrDivider(pt, isLine) {
    const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
    const snapT = spi >= 3 ? (1 / 12) : (4 / 12);
    // For dividing lines, endpoints may be offset up to wallThickness from centerline
    const roomSnapT = isLine ? snapT + this.wallThickness : snapT;
    // Room walls (endpoints + T-intersections)
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      for (const wall of room.walls) {
        for (const wp of [wall.start, wall.end]) {
          if (this.getDistance(pt, wp) < roomSnapT) return true;
        }
        const param = this.getWallParam(pt, wall);
        const proj = this.paramToPoint(wall, param);
        if (param > 0.01 && param < 0.99 && this.getDistance(pt, proj) < roomSnapT) return true;
      }
    }
    // Committed wall segment groups (endpoints + T-intersections)
    for (const group of this.wallSegmentGroups) {
      // Wall-type chains can only connect to wall-type segments, not lines
      if (!isLine && (group.type || 'wall') === 'line') continue;
      for (const seg of group.segments) {
        for (const sp of [seg.start, seg.end]) {
          if (this.getDistance(pt, sp) < snapT) return true;
        }
        const param = this.getWallParam(pt, seg);
        const proj = this.paramToPoint(seg, param);
        if (param > 0.01 && param < 0.99 && this.getDistance(pt, proj) < snapT) return true;
      }
    }
    return false;
  }

  /** Does the chain close back on its own start (an island / cut-out loop)? */
  _isChainClosed(group) {
    const segs = group && group.segments;
    if (!segs || segs.length < 3) return false;
    return this.getDistance(segs[0].start, segs[segs.length - 1].end) < 0.05;
  }

  _isChainEndpointOnWall() {
    if (!this.currentWallSegmentGroup || this.currentWallSegmentGroup.segments.length === 0) return false;
    const segs = this.currentWallSegmentGroup.segments;
    const ep = segs[segs.length - 1].end;
    const isLine = (this.currentWallSegmentGroup.type || 'wall') === 'line';
    if (this._isPointOnWallOrDivider(ep, isLine)) return true;
    // Own chain start (loop close)
    const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
    const snapT = spi >= 3 ? (1 / 12) : (4 / 12);
    if (segs.length > 1 && this.getDistance(ep, segs[0].start) < snapT) return true;
    return false;
  }

  /** A chain may only be committed if it is anchored to a wall/divider at an end,
   *  or closes back on itself. A free-standing open chain divides nothing — it
   *  would draw a line that no zone boundary follows. */
  _isChainCommittable(group) {
    if (!group || !group.segments || group.segments.length === 0) return true;
    if (this._isChainClosed(group)) return true;
    const isLine = (group.type || 'wall') === 'line';
    const segs = group.segments;
    if (this._isPointOnWallOrDivider(segs[0].start, isLine)) return true;
    if (this._isPointOnWallOrDivider(segs[segs.length - 1].end, isLine)) return true;
    return false;
  }

  // CUSTOM_ROOMDESIGNER_BY_CLAUDE — _warnUnclosedDivider ("Shape Not Closed", with
  // Discard / Continue Drawing) was removed here: Esc and Enter now commit whatever
  // has been drawn, so it could never appear again, and a dialog that reads as live
  // is worse than no dialog. `.rd-incomplete-warning` and its CSS stay — the FREEHAND
  // room warning (_warnIncompleteRoom, above) still uses all of it.

  _handleWallSegmentClick(point, rawPoint) {
    const screenPixelsPerInch = this.basePixelsPerFoot * this.zoomLevel / 12;
    const snapThreshold = screenPixelsPerInch >= 3 ? (1 / 12) : (4 / 12);

    if (!this.currentWall || !this.hasPlacedFirstPoint) {
      // First click — start a new segment chain or extend existing
      let snapPoint = null;

      // Use the visual snap target from the last render frame (green dot position)
      // so clicks always match what the user sees, regardless of grid-snap offset
      if (this._wsVisualSnap) {
        snapPoint = { x: this._wsVisualSnap.x, y: this._wsVisualSnap.y };
      }

      // Snap to existing wall segment group endpoints
      for (const group of this.wallSegmentGroups) {
        // Divider walls can't start on divider lines
        if (this._wallSegmentType === 'wall' && (group.type || 'wall') === 'line') continue;
        for (const seg of group.segments) {
          for (const ep of [seg.start, seg.end]) {
            if (this.getDistance(point, ep) < snapThreshold) { snapPoint = { x: ep.x, y: ep.y }; break; }
          }
          if (snapPoint) break;
        }
        if (snapPoint) break;
      }
      // Snap to in-progress group endpoints
      if (!snapPoint && this.currentWallSegmentGroup) {
        for (const seg of this.currentWallSegmentGroup.segments) {
          for (const ep of [seg.start, seg.end]) {
            if (this.getDistance(point, ep) < snapThreshold) { snapPoint = { x: ep.x, y: ep.y }; break; }
          }
          if (snapPoint) break;
        }
      }
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — doorways are snap targets. Above the wall
      // passes, because a jamb sits ON a wall and the generic midpoint snap would
      // otherwise quantise it to the nearest grid step and miss the frame by inches.
      if (!snapPoint) {
        const _ds = this._findDoorSnap(rawPoint || point, snapThreshold + this.wallThickness);
        if (_ds) snapPoint = { x: _ds.x, y: _ds.y };
      }

      // Snap to room wall endpoints: lines snap to exact corner, dividing walls snap 2" inset
      if (!snapPoint) {
        const isLine = this._wallSegmentType === 'line';
        if (isLine) {
          for (const room of [...this.rooms, this.currentRoom]) {
            if (!room || !room.walls) continue;
            for (const wall of room.walls) {
              const epCheckPt = rawPoint || point;
              const epThreshold = snapThreshold + this.wallThickness;
              for (const ep of [wall.start, wall.end]) {
                if (this.getDistance(epCheckPt, ep) < epThreshold) {
                  snapPoint = { x: ep.x, y: ep.y };
                  break;
                }
              }
              if (snapPoint) break;
            }
            if (snapPoint) break;
          }
        } else {
          // Dividing walls: collect 2" inset points from all walls near the corner, pick closest
          const INSET = 2 / 12;
          let bestDist = Infinity;
          for (const room of [...this.rooms, this.currentRoom]) {
            if (!room || !room.walls) continue;
            for (const wall of room.walls) {
              for (const ep of [wall.start, wall.end]) {
                if (this.getDistance(point, ep) < snapThreshold && !this._hasDoorAtPoint(room, ep)) {
                  const toward = ep === wall.start ? wall.end : wall.start;
                  const dx = toward.x - ep.x, dy = toward.y - ep.y;
                  const len = Math.sqrt(dx * dx + dy * dy);
                  if (len > INSET) {
                    const candidate = { x: ep.x + (dx / len) * INSET, y: ep.y + (dy / len) * INSET };
                    const d = this.getDistance(point, candidate);
                    if (d < bestDist) { bestDist = d; snapPoint = candidate; }
                  }
                }
              }
            }
          }
        }
      }

      // Snap to room wall midpoints (T-intersection), skip door spans (lines exempt)
      if (!snapPoint) {
        const isLine = this._wallSegmentType === 'line';
        for (const room of [...this.rooms, this.currentRoom]) {
          if (!room || !room.walls) continue;
          for (const wall of room.walls) {
            // For dividing lines, use raw position for proximity (handles outside of thick walls)
            const checkPt = isLine ? (rawPoint || point) : point;
            let param = this.getWallParam(checkPt, wall);
            // Dividing walls: clamp param away from room wall endpoints (2" inset)
            if (!isLine) {
              const _wl = this.getDistance(wall.start, wall.end);
              if (_wl > 0) { const _ip = (2/12) / _wl; param = Math.max(_ip, Math.min(1 - _ip, param)); }
            }
            const projPt = this.paramToPoint(wall, param);
            const effThreshold = isLine ? snapThreshold + this.wallThickness : snapThreshold;
            if (param > 0.001 && param < 0.999 && this.getDistance(checkPt, projPt) < effThreshold) {
              // Check if this point is within a door span
              let onDoor = false;
              if (!isLine) {
                for (const door of room.doors) {
                  if (door.wall !== wall) continue;
                  const lo = Math.min(door.hingeParam, door.endParam);
                  const hi = Math.max(door.hingeParam, door.endParam);
                  if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
                }
              }
              if (!onDoor) {
                // Grid-quantize snap for dividing lines — offset to nearest wall edge
                if (isLine) {
                  snapPoint = this._quantizeWallEdgeSnap(wall, param, rawPoint || point, room);
                } else {
                  snapPoint = { x: projPt.x, y: projPt.y };
                }
                break;
              }
            }
          }
          if (snapPoint) break;
        }
      }

      // Snap to committed wall segment midpoints (T-intersection), skip door spans (lines exempt)
      if (!snapPoint) {
        const isLine = this._wallSegmentType === 'line';
        for (const group of this.wallSegmentGroups) {
          // Divider walls can't start on divider lines
          if (this._wallSegmentType === 'wall' && (group.type || 'wall') === 'line') continue;
          for (const seg of group.segments) {
            const param = this.getWallParam(point, seg);
            const projPt = this.paramToPoint(seg, param);
            if (param > 0.001 && param < 0.999 && this.getDistance(point, projPt) < snapThreshold) {
              let onDoor = false;
              if (!isLine) {
                for (const door of group.doors) {
                  if (door.wall !== seg) continue;
                  const lo = Math.min(door.hingeParam, door.endParam);
                  const hi = Math.max(door.hingeParam, door.endParam);
                  if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
                }
              }
              if (!onDoor) { snapPoint = { x: projPt.x, y: projPt.y }; break; }
            }
          }
          if (snapPoint) break;
        }
      }

      // First point normally connects to a wall or dividing wall. A free-standing
      // start is allowed anywhere inside a closed room — it carves an island (a
      // cut-out zone) and must close back on itself to commit, which
      // _finishWallSegmentChain() enforces.
      let freeStart = false;
      if (!snapPoint) {
        if (!this._isPointInsideAnyRoom(point)) {
          this.updateStatus(this._wallSegmentType === 'line'
            ? 'Dividing lines must be placed inside a room.'
            : 'Dividing walls must be placed inside a room.', { error: true });
          return;
        }
        freeStart = true;
        snapPoint = { x: point.x, y: point.y };
      }

      let startPt = snapPoint;

      // Snap to exact room polygon vertex if within tolerance
      // Prevents floating-point drift between paramToPoint and polygon vertices
      const VERTEX_SNAP_FIRST = 0.05; // feet
      for (const room of this.rooms) {
        if (!room || !room.closed) continue;
        const geo = this._getRoomGeoCache(room);
        if (!geo || !geo.roomPolygon) continue;
        for (const v of geo.roomPolygon) {
          if (Math.abs(startPt.x - v.x) < VERTEX_SNAP_FIRST && Math.abs(startPt.y - v.y) < VERTEX_SNAP_FIRST) {
            startPt = { x: v.x, y: v.y };
            break;
          }
        }
      }

      // The render-time green dot also fires for a valid free-standing start, so the
      // snapPoint alone can't tell us whether we're anchored — ask the geometry.
      if (!freeStart && !this._isPointOnWallOrDivider(startPt, this._wallSegmentType === 'line')) {
        freeStart = true;
      }

      // Block starting on a door (lines exempt — they can touch door frames)
      // CUSTOM_ROOMDESIGNER_BY_CLAUDE — `true` = jambs are allowed. Standing in the
      // opening is still refused; meeting its frame is the whole point.
      if (this._wallSegmentType !== 'line' && this._isPointOnAnyDoor(startPt, snapThreshold, true)) {
        this.updateStatus('Start at the edge of the doorway, not inside the opening.', { error: true });
        return;
      }

      if (!this.currentWallSegmentGroup) {
        this.currentWallSegmentGroup = { segments: [], doors: [], type: this._wallSegmentType };
      }
      this.currentWallSegmentGroup._startedConnected = !freeStart;
      this.currentWall = { start: { x: startPt.x, y: startPt.y }, end: { x: startPt.x, y: startPt.y } };
      this.hasPlacedFirstPoint = true;
      this._wsSnapSticky = null;
      // Free-standing island: no wall face to hug, so no inside/outside lock
      if (freeStart) {
        this._wsLineSide = null;
        this.updateStatus('Click to place next point. Close the shape back on the first point to finish the cut-out.');
        this.render();
        return;
      }
      // Lock inside/outside face for dividing lines based on cursor position
      if (this._wallSegmentType === 'line') {
        if (this._wsVisualSnap) {
          // Visual snap was green on the inside face (rendering forces inside before first click)
          // — trust the render-time determination over raw cursor position
          this._wsLineSide = 'inside';
        } else {
          const cursorPt = rawPoint || point;
          let isInside = false;
          for (const room of [...this.rooms, this.currentRoom]) {
            if (!room || !room.closed || room.type !== 'add') continue;
            const geo = this._getRoomGeoCache(room);
            if (geo.roomPolygon && geo.roomPolygon.length >= 3 && this.pointInPolygon(cursorPt.x, cursorPt.y, geo.roomPolygon)) {
              isInside = true; break;
            }
          }
          this._wsLineSide = isInside ? 'inside' : 'outside';
          // TODO: Re-enable exterior dividing lines later (outside-room zone creation).
          // For now, block lines starting on the outside face of perimeter walls.
          if (this._wsLineSide === 'outside') {
            this._wsLineSide = null;
            this.hasPlacedFirstPoint = false;
            this.currentWall = null;
            this.updateStatus('Dividing lines must be placed inside a room.', { error: true });
            return;
          }
        }
      }
      this.updateStatus('Click to place next wall point. Esc or double-click to finish.');
      this.render();
      return;
    }

    // Subsequent click — place a segment
    this._wsEscPendingDiscard = false;
    // Use visual snap target from last render frame when available (green dot position),
    // so clicks match what the user sees regardless of grid-snap offset
    let endPoint = this._wsVisualSnap
      ? { x: this._wsVisualSnap.x, y: this._wsVisualSnap.y }
      : { x: point.x, y: point.y };
    if (!this._wsVisualSnap && (this.shiftHeld || this._shiftToggle)) endPoint = this.constrainToAxis(this.currentWall.start, endPoint);

    // Reject tiny segments
    if (this.getDistance(this.currentWall.start, endPoint) < (1 / 12)) return;

    // Side enforcement for dividing lines — block clicks on the wrong face
    // Skip when visual snap is active: the green dot already resolved the correct face
    if (this._wallSegmentType === 'line' && this._wsLineSide && !this._wsVisualSnap) {
      const cursorPt = rawPoint || endPoint;
      const cursorInside = this._isPointInsideAnyRoom(cursorPt);
      const cursorSide = cursorInside ? 'inside' : 'outside';
      if (cursorSide !== this._wsLineSide) {
        this.updateStatus('Dividing line must stay on the ' + this._wsLineSide + ' of the wall.', { error: true });
        return;
      }
    }

    // Pillar/column collision check (blocks both walls and lines)
    if (this._segmentIntersectsObject(this.currentWall.start, endPoint, 'pillar')) {
      this.updateStatus('Interior wall would cross a pillar or column.', { error: true });
      return;
    }
    // Equipment collision check (blocks walls only — lines are non-physical)
    if (this._wallSegmentType !== 'line' && this._segmentIntersectsObject(this.currentWall.start, endPoint, 'equipment')) {
      this.updateStatus('Dividing wall would cross through equipment.', { error: true });
      return;
    }

    // Door crossing check (lines exempt — they can cross door openings)
    if (this._wallSegmentType !== 'line' && this._segmentCrossesDoor(this.currentWall.start, endPoint)) {
      this.updateStatus('Interior wall would cross through a door.', { error: true });
      return;
    }

    // Outside-room restriction: dividing walls must stay inside a room, and so must
    // free-standing dividing lines (a cut-out can't leave the room it carves).
    // Wall-anchored lines stay exempt — they hug wall faces.
    const _freeChain = this._wallSegmentType === 'line' && !this._wsLineSide;
    if ((this._wallSegmentType === 'wall' || _freeChain) && !this._isPointInsideAnyRoom(endPoint)) {
      this.updateStatus(_freeChain
        ? 'Dividing lines must stay inside the room.'
        : 'Dividing walls must stay inside a room.', { error: true });
      return;
    }

    // Snap endpoint to existing endpoints/walls, tracking external connections
    let snappedEnd = null;
    let snappedToExternal = false;

    // When visual snap is active (green dot), trust the render-computed position.
    // The render code already quantized the snap point and applied face offset;
    // re-snapping from rawPoint would produce a different grid position.
    if (this._wsVisualSnap && this._wallSegmentType === 'line') {
      snappedEnd = { x: endPoint.x, y: endPoint.y };
      snappedToExternal = true;
    }

    // Snap to committed wall segment group endpoints
    const chainIsLine = this._wallSegmentType === 'line';
    for (const group of this.wallSegmentGroups) {
      const groupIsLine = (group.type || 'wall') === 'line';
      for (const seg of group.segments) {
        for (const ep of [seg.start, seg.end]) {
          if (this.getDistance(endPoint, ep) < snapThreshold) { snappedEnd = { x: ep.x, y: ep.y }; break; }
        }
        if (snappedEnd) break;
      }
      if (snappedEnd) {
        // Wall-type chains can't auto-finish on line-type segments
        if (!groupIsLine || chainIsLine) snappedToExternal = true;
        break;
      }
    }

    // Snap to own chain start point (loop close)
    if (!snappedEnd && this.currentWallSegmentGroup.segments.length > 0) {
      const chainStart = this.currentWallSegmentGroup.segments[0].start;
      if (this.getDistance(endPoint, chainStart) < snapThreshold) {
        snappedEnd = { x: chainStart.x, y: chainStart.y };
      }
    }
    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — finish ON a doorway. Landing on a jamb counts
    // as reaching a wall (snappedToExternal), so a chain drawn wall-to-window
    // auto-completes exactly as one drawn wall-to-wall does. See _getDoorSnapPoints.
    if (!snappedEnd) {
      const _ds = this._findDoorSnap(rawPoint || endPoint, snapThreshold + this.wallThickness);
      if (_ds) {
        snappedEnd = { x: _ds.x, y: _ds.y };
        if (_ds.room && _ds.room.walls) snappedToExternal = true;
      }
    }

    // Snap to room wall endpoints: lines snap to exact corner, dividing walls snap 2" inset
    if (!snappedEnd) {
      const isLine = this._wallSegmentType === 'line';
      if (isLine) {
        for (const room of [...this.rooms, this.currentRoom]) {
          if (!room || !room.walls) continue;
          for (const wall of room.walls) {
            const epCheckPt = rawPoint || endPoint;
            const epThreshold = snapThreshold + this.wallThickness;
            for (const ep of [wall.start, wall.end]) {
              if (this.getDistance(epCheckPt, ep) < epThreshold) {
                snappedEnd = { x: ep.x, y: ep.y }; snappedToExternal = true; break;
              }
            }
            if (snappedEnd) break;
          }
          if (snappedEnd) break;
        }
      } else {
        const INSET = 2 / 12;
        let bestDist = Infinity;
        for (const room of [...this.rooms, this.currentRoom]) {
          if (!room || !room.walls) continue;
          for (const wall of room.walls) {
            for (const ep of [wall.start, wall.end]) {
              if (this.getDistance(endPoint, ep) < snapThreshold && !this._hasDoorAtPoint(room, ep)) {
                const toward = ep === wall.start ? wall.end : wall.start;
                const dx = toward.x - ep.x, dy = toward.y - ep.y;
                const len = Math.sqrt(dx * dx + dy * dy);
                if (len > INSET) {
                  const candidate = { x: ep.x + (dx / len) * INSET, y: ep.y + (dy / len) * INSET };
                  const d = this.getDistance(endPoint, candidate);
                  if (d < bestDist) { bestDist = d; snappedEnd = candidate; snappedToExternal = true; }
                }
              }
            }
          }
        }
      }
    }
    // Snap to room wall midpoints (T-intersection), skip door spans (lines exempt)
    if (!snappedEnd) {
      const isLine = this._wallSegmentType === 'line';
      for (const room of [...this.rooms, this.currentRoom]) {
        if (!room || !room.walls) continue;
        for (const wall of room.walls) {
          // For dividing lines, use raw position for proximity (handles outside of thick walls)
          const checkPt = isLine ? (rawPoint || endPoint) : endPoint;
          let param = this.getWallParam(checkPt, wall);
          // Dividing walls: clamp param away from room wall endpoints (2" inset)
          if (!isLine) {
            const _wl = this.getDistance(wall.start, wall.end);
            if (_wl > 0) { const _ip = (2/12) / _wl; param = Math.max(_ip, Math.min(1 - _ip, param)); }
          }
          const projPt = this.paramToPoint(wall, param);
          const effThreshold = isLine ? snapThreshold + this.wallThickness : snapThreshold;
          if (param > 0.01 && param < 0.99 && this.getDistance(checkPt, projPt) < effThreshold) {
            let onDoor = false;
            if (!isLine) {
              for (const door of room.doors) {
                if (door.wall !== wall) continue;
                const lo = Math.min(door.hingeParam, door.endParam);
                const hi = Math.max(door.hingeParam, door.endParam);
                if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
              }
            }
            if (!onDoor) {
              // Grid-quantize snap for dividing lines — offset to nearest wall edge
              if (isLine) {
                snappedEnd = this._quantizeWallEdgeSnap(wall, param, rawPoint || endPoint, room);
              } else {
                snappedEnd = { x: projPt.x, y: projPt.y };
              }
              snappedToExternal = true; break;
            }
          }
        }
        if (snappedEnd) break;
      }
    }
    // Snap to committed wall segment midpoints (T-intersection), skip door spans (lines exempt)
    if (!snappedEnd) {
      const isLine = this._wallSegmentType === 'line';
      for (const group of this.wallSegmentGroups) {
        const gIsLine = (group.type || 'wall') === 'line';
        for (const seg of group.segments) {
          const param = this.getWallParam(endPoint, seg);
          const projPt = this.paramToPoint(seg, param);
          if (param > 0.01 && param < 0.99 && this.getDistance(endPoint, projPt) < snapThreshold) {
            let onDoor = false;
            if (!isLine) {
              for (const door of group.doors) {
                if (door.wall !== seg) continue;
                const lo = Math.min(door.hingeParam, door.endParam);
                const hi = Math.max(door.hingeParam, door.endParam);
                if (param >= lo - 0.02 && param <= hi + 0.02) { onDoor = true; break; }
              }
            }
            if (!onDoor) {
              snappedEnd = { x: projPt.x, y: projPt.y };
              if (!gIsLine || chainIsLine) snappedToExternal = true;
              break;
            }
          }
        }
        if (snappedEnd) break;
      }
    }

    if (snappedEnd) endPoint = snappedEnd;

    // Snap to exact room polygon vertex if within tolerance
    // Prevents floating-point drift between paramToPoint and polygon vertices
    const VERTEX_SNAP_END = 0.05; // feet
    for (const room of this.rooms) {
      if (!room || !room.closed) continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo || !geo.roomPolygon) continue;
      for (const v of geo.roomPolygon) {
        if (Math.abs(endPoint.x - v.x) < VERTEX_SNAP_END && Math.abs(endPoint.y - v.y) < VERTEX_SNAP_END) {
          endPoint = { x: v.x, y: v.y };
          break;
        }
      }
    }

    // Block ending on a door (lines exempt — they can touch door frames)
    if (this._wallSegmentType !== 'line' && this._isPointOnAnyDoor(endPoint, snapThreshold, true)) {
      this.updateStatus('Finish at the edge of the doorway, not inside the opening.', { error: true });
      return;
    }

    // Skip undo save if collinear with previous segment (undo removes both at once)
    const prevSegs = this.currentWallSegmentGroup.segments;
    const lastSeg = prevSegs.length > 0 ? prevSegs[prevSegs.length - 1] : null;
    let collinear = false;
    if (lastSeg) {
      const ldx = lastSeg.end.x - lastSeg.start.x, ldy = lastSeg.end.y - lastSeg.start.y;
      const ndx = endPoint.x - this.currentWall.start.x, ndy = endPoint.y - this.currentWall.start.y;
      const cross = ldx * ndy - ldy * ndx;
      const dot = ldx * ndx + ldy * ndy;
      collinear = Math.abs(cross) < 0.001 && dot > 0;
    }
    if (!collinear) this.saveUndoState();
    this.currentWallSegmentGroup.segments.push({
      start: { x: this.currentWall.start.x, y: this.currentWall.start.y },
      end: { x: endPoint.x, y: endPoint.y }
    });
    // Merge collinear segments into single longer segments
    const mergeWrap = { walls: this.currentWallSegmentGroup.segments };
    this.mergeCollinearWalls(mergeWrap);
    this.currentWallSegmentGroup.segments = mergeWrap.walls;

    // Auto-finish: loop closed back to own start point
    const chainStart = this.currentWallSegmentGroup.segments[0].start;
    const isLoopClose = Math.abs(endPoint.x - chainStart.x) < 0.001 && Math.abs(endPoint.y - chainStart.y) < 0.001
      && this.currentWallSegmentGroup.segments.length > 1;
    // Auto-finish: started on a wall/segment and ended on a different wall/segment
    const isWallToWall = this.currentWallSegmentGroup._startedConnected && snappedToExternal;

    if (isLoopClose || isWallToWall) {
      const hidden = this._finishWallSegmentChain();
      if (hidden) this.updateStatus('Dividing line hidden — enable Seam Lines in Settings to see it', { urgent: true });
      else this.updateStatus('Interior wall complete.');
      this.render();
      return;
    }

    // Continue chain from endpoint
    this.currentWall = { start: { x: endPoint.x, y: endPoint.y }, end: { x: endPoint.x, y: endPoint.y } };
    this.updateStatus('Segment placed. Click next point, or Esc/double-click to finish.');
    this.render();
  }

  _segmentIntersectsObject(start, end, filter) {
    for (const obj of this.objects) {
      if (filter === 'pillar' && obj.type !== 'circle' && obj.type !== 'rectangle') continue;
      if (filter === 'equipment' && obj.type !== 'equipment') continue;
      if (obj.type === 'circle') {
        const cx = obj.x, cy = obj.y, r = obj.diameter / 2;
        if (this._segmentIntersectsCircle(start, end, { x: cx, y: cy }, r)) return true;
      } else if (obj.type === 'rectangle' || obj.type === 'equipment') {
        const rot = obj.rotation || 0;
        const hw = obj.width / 2, hd = obj.depth / 2;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const corners = [
          { x: obj.x + cos * (-hw) - sin * (-hd), y: obj.y + sin * (-hw) + cos * (-hd) },
          { x: obj.x + cos * (hw) - sin * (-hd), y: obj.y + sin * (hw) + cos * (-hd) },
          { x: obj.x + cos * (hw) - sin * (hd), y: obj.y + sin * (hw) + cos * (hd) },
          { x: obj.x + cos * (-hw) - sin * (hd), y: obj.y + sin * (-hw) + cos * (hd) }
        ];
        if (this._segmentIntersectsRect(start, end, corners)) return true;
      }
    }
    return false;
  }

  _segmentCrossesDoor(start, end) {
    const checkDoors = (doors) => {
      for (const door of doors) {
        const hp = this.paramToPoint(door.wall, door.hingeParam);
        const ep = this.paramToPoint(door.wall, door.endParam);
        if (this._segmentsIntersectPts(start, end, hp, ep)) return true;
      }
      return false;
    };
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.doors) continue;
      if (checkDoors(room.doors)) return true;
    }
    for (const group of this.wallSegmentGroups) {
      if (checkDoors(group.doors)) return true;
    }
    return false;
  }

  _quantizeWallSnap(wall, param) {
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const wallLen = Math.sqrt(dx * dx + dy * dy);
    if (wallLen < 0.01) return this.paramToPoint(wall, param);
    const dist = param * wallLen;
    const spi = this.basePixelsPerFoot * this.zoomLevel / 12;
    const fine = spi >= 3;
    const metric = this.unit === 'meters' || this.unit === 'm-cm' || this.unit === 'cm';
    const gridFt = metric ? (fine ? 0.032808399 : 0.82020997) : (fine ? 0.083333333 : 1);
    const snappedDist = Math.round(dist / gridFt) * gridFt;
    // Clamp to wall length
    const clampedDist = Math.max(0, Math.min(snappedDist, wallLen));
    const t = clampedDist / wallLen;
    return { x: wall.start.x + dx * t, y: wall.start.y + dy * t };
  }

  _quantizeWallEdgeSnap(wall, param, cursorPt, room) {
    const centerPt = this._quantizeWallSnap(wall, param);
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.01) return { x: centerPt.x, y: centerPt.y };
    const nx = -dy / len, ny = dx / len;
    // Determine outward direction from room polygon
    let outSign = 1;
    if (room) {
      const geo = this._getRoomGeoCache(room);
      if (geo.roomPolygon && geo.roomPolygon.length >= 3) {
        const testPt = { x: centerPt.x + nx * 0.05, y: centerPt.y + ny * 0.05 };
        if (this.pointInPolygon(testPt.x, testPt.y, geo.roomPolygon)) outSign = -1;
        // Inner face = centerline, Outer face = full wallThickness outward
        const insideEdge = { x: centerPt.x, y: centerPt.y };
        const outsideEdge = { x: centerPt.x + nx * outSign * this.wallThickness, y: centerPt.y + ny * outSign * this.wallThickness };
        // Determine face: locked side > cursor position in polygon
        // TODO: When exterior dividing lines are re-enabled, restore the
        // else branch to: this.pointInPolygon(cursorPt.x, cursorPt.y, geo.roomPolygon)
        let showInside;
        if (this._wsLineSide === 'inside') showInside = true;
        else if (this._wsLineSide === 'outside') showInside = false;
        else showInside = true; // exterior lines disabled — always snap inside
        return showInside ? insideEdge : outsideEdge;
      }
    }
    return { x: centerPt.x, y: centerPt.y };
  }

  _isPointInsideAnyRoom(point) {
    for (const room of this.rooms) {
      if (!room || !room.closed || room.type !== 'add') continue;
      const geo = this._getRoomGeoCache(room);
      if (!geo.roomPolygon || geo.roomPolygon.length < 3) continue;
      if (this.pointInPolygon(point.x, point.y, geo.roomPolygon)) return true;
      // Boundary tolerance: accept points on room walls (within 0.1 ft)
      for (let i = 0; i < geo.roomPolygon.length; i++) {
        const j = (i + 1) % geo.roomPolygon.length;
        const wall = { start: geo.roomPolygon[i], end: geo.roomPolygon[j] };
        if (RoomDesignerMath.distanceToWall(point, wall) < 0.1) return true;
      }
    }
    return false;
  }

  /**
   * CUSTOM_ROOMDESIGNER_BY_CLAUDE — snap targets contributed by DOORS: both jambs
   * and the centre of the opening.
   *
   * A zone boundary very often wants to land on a doorway, and on a ROLLER most of
   * all — a big window is drawn as a roller, and the zone should stop AT the window
   * rather than a foot short of it. Until now the snap chain actively avoided doors:
   * every wall-midpoint pass skipped anything inside a door span, so the one place
   * you most wanted to land was the one place nothing would catch you.
   */
  _getDoorSnapPoints() {
    const out = [];
    const add = (owner, doors) => {
      for (const d of (doors || [])) {
        if (!d.wall) continue;
        const lo = Math.min(d.hingeParam, d.endParam);
        const hi = Math.max(d.hingeParam, d.endParam);
        for (const [p, kind] of [[lo, 'jamb'], [hi, 'jamb'], [(lo + hi) / 2, 'center']]) {
          const pt = this.paramToPoint(d.wall, p);
          out.push({ x: pt.x, y: pt.y, wall: d.wall, param: p, room: owner, door: d, snapType: kind });
        }
      }
    };
    for (const room of [...this.rooms, this.currentRoom]) { if (room) add(room, room.doors); }
    for (const g of this.wallSegmentGroups) add(g, g.doors);
    return out;
  }

  /** Nearest door jamb/centre within `threshold`, or null. */
  _findDoorSnap(point, threshold) {
    let best = null, bestD = threshold;
    for (const s of this._getDoorSnapPoints()) {
      const d = this.getDistance(point, s);
      if (d < bestD) { bestD = d; best = s; }
    }
    return best;
  }

  _isPointOnAnyDoor(point, snapThreshold, allowJambs) {
    const check = (walls, doors) => {
      for (const wall of walls) {
        const param = this.getWallParam(point, wall);
        const proj = this.paramToPoint(wall, param);
        if (this.getDistance(point, proj) > snapThreshold) continue;
        for (const door of doors) {
          if (door.wall !== wall) continue;
          const lo = Math.min(door.hingeParam, door.endParam);
          const hi = Math.max(door.hingeParam, door.endParam);
          // CUSTOM_ROOMDESIGNER_BY_CLAUDE — the EDGE of an opening is a legal place
          // to meet, even for a dividing wall. Only the span between the jambs is
          // out of bounds: a wall may butt into a door frame, not stand in the gap.
          if (allowJambs) {
            const a = this.paramToPoint(wall, lo), b = this.paramToPoint(wall, hi);
            if (this.getDistance(point, a) < 1 / 12 || this.getDistance(point, b) < 1 / 12) continue;
          }
          if (param >= lo - 0.02 && param <= hi + 0.02) return true;
        }
      }
      return false;
    };
    for (const room of [...this.rooms, this.currentRoom]) {
      if (!room || !room.walls) continue;
      if (check(room.walls, room.doors)) return true;
    }
    for (const group of this.wallSegmentGroups) {
      if (check(group.segments, group.doors)) return true;
    }
    return false;
  }

  /**
   * @param wallsOnly CUSTOM_ROOMDESIGNER_BY_CLAUDE — bands only, no labels and no
   * in-progress preview. Second pass after objects so turf underlaps a DIVIDING wall
   * the same way it underlaps a room wall. Same reasoning as drawRoom's wallsOnly.
   */
  _drawWallSegmentGroups(wallsOnly) {
    const scale = this.basePixelsPerFoot * this.zoomLevel;
    const segPixelWidth = Math.max(1.5, this.wallSegmentThickness * scale);
    const wallLW = segPixelWidth / scale;
    const tc = this._tc || this._themeColors();
    const _segOverlay = window.roomDesignerOverlay;
    const _segTracing = this._bgTraceActive && (!_segOverlay || _segOverlay.currentStep === 'draw');
    // Deferred gaps: erase connecting wall's near line at T-junctions after all groups drawn
    const deferredGaps = [];
    const _wsgFocusPoly = this._printFocusRoom ? (this._getRoomGeoCache(this._printFocusRoom).roomPolygon || null) : null;
    // Shared: build connected paths from segments, then draw solid or hollow
    const drawSegmentGroup = (segments, color, isLine, alphaOverride) => {
      if (!segments || segments.length === 0) return;
      const key = (p) => `${(Math.round(p.x * 1200) / 1200).toFixed(4)},${(Math.round(p.y * 1200) / 1200).toFixed(4)}`;
      // Build adjacency and trace connected paths
      const undrawn = new Set(segments.map((_, i) => i));
      const startAdj = {}, endAdj = {};
      segments.forEach((seg, idx) => {
        const sk = key(seg.start), ek = key(seg.end);
        if (!startAdj[sk]) startAdj[sk] = []; if (!endAdj[ek]) endAdj[ek] = [];
        startAdj[sk].push(idx); endAdj[ek].push(idx);
      });
      while (undrawn.size > 0) {
        const startIdx = undrawn.values().next().value;
        undrawn.delete(startIdx);
        const path = [segments[startIdx].start, segments[startIdx].end];
        let current = key(segments[startIdx].end);
        while (true) {
          let found = false;
          for (const idx of (startAdj[current] || [])) {
            if (undrawn.has(idx)) { undrawn.delete(idx); path.push(segments[idx].end); current = key(segments[idx].end); found = true; break; }
          }
          if (!found) {
            for (const idx of (endAdj[current] || [])) {
              if (undrawn.has(idx)) { undrawn.delete(idx); path.push(segments[idx].start); current = key(segments[idx].start); found = true; break; }
            }
          }
          if (!found) break;
        }
        if (path.length < 2) continue;
        const isClosed = path.length >= 4 && Math.abs(path[0].x - path[path.length - 1].x) < 0.05 && Math.abs(path[0].y - path[path.length - 1].y) < 0.05;
        // Miter-joined offset path helper
        const offsetPath = (sign, dist) => {
          const pts = [];
          const n = path.length;
          for (let i = 0; i < n; i++) {
            const prev = (i > 0) ? i - 1 : (isClosed ? n - 2 : -1);
            const next = (i < n - 1) ? i + 1 : (isClosed ? 1 : -1);
            let nx = 0, ny = 0;
            if (prev >= 0 && next >= 0) {
              const d1x = path[i].x - path[prev].x, d1y = path[i].y - path[prev].y;
              const len1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
              const n1x = -d1y / len1 * sign, n1y = d1x / len1 * sign;
              const d2x = path[next].x - path[i].x, d2y = path[next].y - path[i].y;
              const len2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
              const n2x = -d2y / len2 * sign, n2y = d2x / len2 * sign;
              const dot = n1x * n2x + n1y * n2y;
              if (Math.abs(1 + dot) < 0.001) {
                nx = n1x * dist; ny = n1y * dist;
              } else {
                const mx = (n1x + n2x) / 2, my = (n1y + n2y) / 2;
                const mLen = Math.sqrt(mx * mx + my * my) || 1;
                const cosHalf = (mx * n1x + my * n1y) / mLen;
                const miterLen = cosHalf !== 0 ? dist / cosHalf : dist;
                const clampedLen = Math.min(miterLen, dist * 4);
                nx = mx / mLen * clampedLen; ny = my / mLen * clampedLen;
              }
            } else if (prev >= 0) {
              const dx = path[i].x - path[prev].x, dy = path[i].y - path[prev].y;
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              nx = -dy / len * sign * dist; ny = dx / len * sign * dist;
            } else if (next >= 0) {
              const dx = path[next].x - path[i].x, dy = path[next].y - path[i].y;
              const len = Math.sqrt(dx * dx + dy * dy) || 1;
              nx = -dy / len * sign * dist; ny = dx / len * sign * dist;
            }
            pts.push({ x: path[i].x + nx, y: path[i].y + ny });
          }
          return pts;
        };
        const strokePath = (pts, close) => {
          this.ctx.beginPath();
          this.ctx.moveTo(pts[0].x, pts[0].y);
          for (let pi = 1; pi < pts.length; pi++) this.ctx.lineTo(pts[pi].x, pts[pi].y);
          if (close) this.ctx.closePath();
          this.ctx.stroke();
        };
        this.ctx.strokeStyle = color;
        this.ctx.lineJoin = 'miter'; this.ctx.miterLimit = 10;
        if (isLine) {
          // Trim endpoints where line connects to a wall-type segment (hide line inside wall)
          if (!isClosed && path.length >= 2) {
            const sht = this.wallSegmentThickness / 2;
            const trimEndpoint = (ptIdx, adjIdx) => {
              const pt = path[ptIdx], adj = path[adjIdx];
              for (const grp of this.wallSegmentGroups) {
                if (grp.type === 'line') continue;
                for (const w of grp.segments) {
                  if (this.getDistance(pt, w.start) < 0.01 || this.getDistance(pt, w.end) < 0.01) continue;
                  const param = this.getWallParam(pt, w);
                  const proj = this.paramToPoint(w, param);
                  if (this.getDistance(pt, proj) >= 0.05) continue;
                  // Found T-junction — compute near edge of this wall
                  const wdx = w.end.x - w.start.x, wdy = w.end.y - w.start.y;
                  const wl = Math.sqrt(wdx * wdx + wdy * wdy) || 1;
                  const wDir = { x: wdx / wl, y: wdy / wl };
                  const adx = pt.x - adj.x, ady = pt.y - adj.y;
                  const al = Math.sqrt(adx * adx + ady * ady) || 1;
                  const aDir = { x: adx / al, y: ady / al };
                  const n1 = { x: -wDir.y, y: wDir.x };
                  const dot = n1.x * (-aDir.x) + n1.y * (-aDir.y);
                  const innerN = dot > 0 ? n1 : { x: wDir.y, y: -wDir.x };
                  const target = { x: pt.x + innerN.x * sht, y: pt.y + innerN.y * sht };
                  const cross = aDir.x * wDir.y - aDir.y * wDir.x;
                  if (Math.abs(cross) < 0.0001) return;
                  const dx = target.x - adj.x, dy = target.y - adj.y;
                  const t = (dx * wDir.y - dy * wDir.x) / cross;
                  path[ptIdx] = { x: adj.x + t * aDir.x, y: adj.y + t * aDir.y };
                  return;
                }
              }
            };
            trimEndpoint(0, 1);
            trimEndpoint(path.length - 1, path.length - 2);
          }
          // Dividing lines: thin single stroke, tight dashes, slightly faint
          // In print mode, render at alpha 0 so the stroke path still runs
          // (keeps clip/path state consistent) but produces no visible output.
          this.ctx.lineWidth = 1.5 / scale;
          this.ctx.lineCap = 'butt';
          this.ctx.setLineDash([4 / scale, 4.5 / scale]);
          const prevAlpha = this.ctx.globalAlpha;
          if (this._printMode || this._viewOnly) {
            if (isClosed) {
              // Closed loop = an island cut-out, i.e. a real flooring-material
              // boundary — keep it visible in presentation/PDF as a solid hairline.
              this.ctx.setLineDash([]);
              this.ctx.globalAlpha = 0.85;
            } else {
              this.ctx.globalAlpha = 0;
            }
          } else if (_segTracing) {
            this.ctx.strokeStyle = '#ffffff';
            this.ctx.globalAlpha = 0.8;
          } else {
            this.ctx.globalAlpha = alphaOverride != null ? alphaOverride : 0.6;
          }
          strokePath(path, isClosed);
          this.ctx.globalAlpha = prevAlpha;
          this.ctx.setLineDash([]);
        } else if (_segTracing) {
          // Trace mode: solid blue fill + white outline for wall segments
          const halfT = this.wallSegmentThickness / 2;
          const outerPts = offsetPath(1, halfT);
          const innerPts = offsetPath(-1, halfT);
          this.ctx.beginPath();
          this.ctx.moveTo(outerPts[0].x, outerPts[0].y);
          for (let pi = 1; pi < outerPts.length; pi++) this.ctx.lineTo(outerPts[pi].x, outerPts[pi].y);
          for (let pi = innerPts.length - 1; pi >= 0; pi--) this.ctx.lineTo(innerPts[pi].x, innerPts[pi].y);
          this.ctx.closePath();
          this.ctx.fillStyle = '#006BCC';
          this.ctx.fill();
          this.ctx.strokeStyle = '#ffffff';
          this.ctx.lineWidth = 1.5 / scale;
          this.ctx.stroke();
        } else if (this.hollowWalls) {
          const thinWidth = 1.5 / scale;
          this.ctx.lineWidth = thinWidth;
          this.ctx.lineCap = 'butt';
          const halfT = this.wallSegmentThickness / 2;
          const outerPts = offsetPath(1, halfT);
          const innerPts = offsetPath(-1, halfT);
          // At connected endpoints, project offset lines to the connecting wall's near edge
          // so the divider butts cleanly against it at any angle (no overlap/gap).
          // Room walls: project to centerline (offset=0, inner face is at centerline).
          // Segment walls: project to near offset line (offset=halfT from centerline toward approach).
          let startConnected = false, endConnected = false;
          if (!isClosed && path.length >= 2) {
            // Find the most perpendicular wall at an endpoint; return its direction + edge offset
            const findConn = (pt, approachFrom) => {
              const adx = approachFrom.x - pt.x, ady = approachFrom.y - pt.y;
              const al = Math.sqrt(adx * adx + ady * ady) || 1;
              let best = null, bestCross = 0;
              const tryWall = (w, offset) => {
                const param = this.getWallParam(pt, w);
                const proj = this.paramToPoint(w, param);
                if (this.getDistance(pt, proj) >= 0.05) return;
                const wdx = w.end.x - w.start.x, wdy = w.end.y - w.start.y;
                const wl = Math.sqrt(wdx * wdx + wdy * wdy) || 1;
                const absCross = Math.abs((adx / al) * (wdy / wl) - (ady / al) * (wdx / wl));
                if (absCross <= bestCross) return;
                bestCross = absCross;
                best = { dir: { x: wdx / wl, y: wdy / wl }, offset: offset };
              };
              // Check segment walls first — divider-to-divider takes priority
              // Skip by endpoint (not by group) so T-junctions within the same group are detected
              const sht = this.wallSegmentThickness / 2;
              for (const grp of this.wallSegmentGroups) {
                if (grp.type === 'line') continue;
                for (const w of grp.segments) {
                  // Skip walls where this point is at one of the wall's own endpoints (adjacent in chain)
                  if (this.getDistance(pt, w.start) < 0.01 || this.getDistance(pt, w.end) < 0.01) continue;
                  tryWall(w, sht);
                }
              }
              // Only check room walls if no segment match found
              if (!best) {
                for (const room of [...this.rooms, this.currentRoom]) {
                  if (!room || !room.walls) continue;
                  for (const w of room.walls) tryWall(w, 0);
                }
              }
              return best;
            };
            // Project both offset points along path direction to the connecting wall's near edge
            const projectToWall = (conn, oi, ii, cPt, pDir) => {
              const cross = pDir.x * conn.dir.y - pDir.y * conn.dir.x;
              if (Math.abs(cross) < 0.0001) return false;
              // For segments, shift target from centerline to near edge (halfT toward approach)
              let target = cPt;
              if (conn.offset > 0) {
                const n1 = { x: -conn.dir.y, y: conn.dir.x };
                // pDir points toward the wall; approach body is at -pDir
                const dot = n1.x * (-pDir.x) + n1.y * (-pDir.y);
                const innerN = dot > 0 ? n1 : { x: conn.dir.y, y: -conn.dir.x };
                target = { x: cPt.x + innerN.x * conn.offset, y: cPt.y + innerN.y * conn.offset };
              }
              const odx = target.x - outerPts[oi].x, ody = target.y - outerPts[oi].y;
              const tO = (odx * conn.dir.y - ody * conn.dir.x) / cross;
              outerPts[oi] = { x: outerPts[oi].x + tO * pDir.x, y: outerPts[oi].y + tO * pDir.y };
              const idx = target.x - innerPts[ii].x, idy = target.y - innerPts[ii].y;
              const tI = (idx * conn.dir.y - idy * conn.dir.x) / cross;
              innerPts[ii] = { x: innerPts[ii].x + tI * pDir.x, y: innerPts[ii].y + tI * pDir.y };
              return true;
            };
            const startConn = findConn(path[0], path[1]);
            if (startConn) {
              const dx = path[0].x - path[1].x, dy = path[0].y - path[1].y;
              const l = Math.sqrt(dx * dx + dy * dy) || 1;
              startConnected = projectToWall(startConn, 0, 0, path[0], { x: dx / l, y: dy / l });
            }
            const endConn = findConn(path[path.length - 1], path[path.length - 2]);
            if (endConn) {
              const n = path.length;
              const dx = path[n - 1].x - path[n - 2].x, dy = path[n - 1].y - path[n - 2].y;
              const l = Math.sqrt(dx * dx + dy * dy) || 1;
              endConnected = projectToWall(endConn, outerPts.length - 1, innerPts.length - 1, path[n - 1], { x: dx / l, y: dy / l });
            }
            // Collect gap info: erase connecting wall's near line between projected offset points
            if (startConnected) {
              deferredGaps.push({ from: { x: outerPts[0].x, y: outerPts[0].y }, to: { x: innerPts[0].x, y: innerPts[0].y } });
            }
            if (endConnected) {
              const li = outerPts.length - 1;
              deferredGaps.push({ from: { x: outerPts[li].x, y: outerPts[li].y }, to: { x: innerPts[li].x, y: innerPts[li].y } });
            }
          }
          // Fill hollow wall segment channels with background color (no grid inside)
          this.ctx.fillStyle = this.darkMode ? '#141c2e' : '#ffffff';
          this.ctx.beginPath();
          this.ctx.moveTo(outerPts[0].x, outerPts[0].y);
          for (let pi = 1; pi < outerPts.length; pi++) this.ctx.lineTo(outerPts[pi].x, outerPts[pi].y);
          for (let pi = innerPts.length - 1; pi >= 0; pi--) this.ctx.lineTo(innerPts[pi].x, innerPts[pi].y);
          this.ctx.closePath();
          this.ctx.fill();
          strokePath(outerPts, isClosed);
          strokePath(innerPts, isClosed);
          // End caps only at unconnected endpoints
          if (!isClosed && path.length >= 2) {
            if (!startConnected) {
              this.ctx.beginPath();
              this.ctx.moveTo(outerPts[0].x, outerPts[0].y);
              this.ctx.lineTo(innerPts[0].x, innerPts[0].y);
              this.ctx.stroke();
            }
            if (!endConnected) {
              this.ctx.beginPath();
              this.ctx.moveTo(outerPts[outerPts.length - 1].x, outerPts[outerPts.length - 1].y);
              this.ctx.lineTo(innerPts[innerPts.length - 1].x, innerPts[innerPts.length - 1].y);
              this.ctx.stroke();
            }
          }
        } else {
          this.ctx.lineWidth = wallLW;
          this.ctx.lineCap = 'butt';
          strokePath(path, isClosed);
        }
      }
    };

    // Draw committed groups (always fully opaque, even in floor mode)
    for (const group of this.wallSegmentGroups) {
      // Skip dividing lines when seam lines toggle is off (floor tab or view-only override)
      // On equipment tab with seams off: still show dividers faintly (25% alpha)
      const _onCustomizeTab = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
      if (group.type === 'line' && !this.showSeamLines && (this._floorMode || _onCustomizeTab || this._viewOnlyHideZoneDividers)) continue;
      const _faintDivider = group.type === 'line' && !this.showSeamLines && this._equipmentMode;
      // In per-space PDF mode, only draw groups whose segments fall inside the focus room
      if (_wsgFocusPoly) {
        let _groupInside = false;
        for (const seg of group.segments) {
          const mx = (seg.start.x + seg.end.x) / 2;
          const my = (seg.start.y + seg.end.y) / 2;
          if (this.pointInPolygon(mx, my, _wsgFocusPoly)) { _groupInside = true; break; }
        }
        if (!_groupInside) continue;
      }
      // Snapshot before walls so door gaps can restore underlying content
      if (group.type !== 'line' && group.doors && group.doors.length > 0) {
        if (!this._doorSnapCanvas || this._doorSnapCanvas.width !== this.canvas.width || this._doorSnapCanvas.height !== this.canvas.height) {
          this._doorSnapCanvas = document.createElement('canvas');
          this._doorSnapCanvas.width = this.canvas.width;
          this._doorSnapCanvas.height = this.canvas.height;
        }
        this._doorSnapCanvas.getContext('2d').drawImage(this.canvas, 0, 0);
      }
      drawSegmentGroup(group.segments, tc.wallColor, group.type === 'line', _faintDivider ? 0.20 : undefined);
      if (group.type !== 'line') {
        for (const door of group.doors) {
          this.drawDoor(door, group);
        }
      }
    }

    // Draw in-progress group (brand blue) — skip line-type if seam lines hidden (floor or equipment tab)
    if (this.currentWallSegmentGroup && this.currentWallSegmentGroup.segments.length > 0) {
      if (this.currentWallSegmentGroup.type !== 'line' || this.showSeamLines || !(this._floorMode || this._equipmentMode)) {
        drawSegmentGroup(this.currentWallSegmentGroup.segments, '#006BCC', this.currentWallSegmentGroup.type === 'line');
      }
    }

    // Erase connecting wall's near line at T-junctions (gap the inner line)
    if (deferredGaps.length > 0) {
      const bgColor = this.darkMode ? '#141c2e' : '#ffffff';
      const thinWidth = 1.5 / scale;
      const gapHalfW = thinWidth;
      this.ctx.strokeStyle = bgColor;
      this.ctx.lineWidth = thinWidth * 2;
      this.ctx.lineCap = 'butt';
      this.ctx.setLineDash([]);
      for (const gap of deferredGaps) {
        const dx = gap.to.x - gap.from.x, dy = gap.to.y - gap.from.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const ux = dx / len, uy = dy / len;
        const shrink = 1 / scale;
        this.ctx.beginPath();
        this.ctx.moveTo(gap.from.x + ux * shrink, gap.from.y + uy * shrink);
        this.ctx.lineTo(gap.to.x - ux * shrink, gap.to.y - uy * shrink);
        this.ctx.stroke();
      }
      // Redraw grid inside gap regions so grid lines aren't lost under the erase
      if (this.showGrid) {
        const spi = scale / 12;
        for (const gap of deferredGaps) {
          const dx = gap.to.x - gap.from.x, dy = gap.to.y - gap.from.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const ux = dx / len, uy = dy / len;
          const px = -uy, py = ux; // perpendicular
          const shrink = 1 / scale;
          const ax = gap.from.x + ux * shrink, ay = gap.from.y + uy * shrink;
          const bx = gap.to.x - ux * shrink, by = gap.to.y - uy * shrink;
          this.ctx.save();
          this.ctx.beginPath();
          this.ctx.moveTo(ax + px * gapHalfW, ay + py * gapHalfW);
          this.ctx.lineTo(bx + px * gapHalfW, by + py * gapHalfW);
          this.ctx.lineTo(bx - px * gapHalfW, by - py * gapHalfW);
          this.ctx.lineTo(ax - px * gapHalfW, ay - py * gapHalfW);
          this.ctx.closePath();
          this.ctx.clip();
          const gMinX = Math.min(ax, bx) - gapHalfW, gMinY = Math.min(ay, by) - gapHalfW;
          const gMaxX = Math.max(ax, bx) + gapHalfW, gMaxY = Math.max(ay, by) + gapHalfW;
          this.ctx.strokeStyle = tc.gridMajor; this.ctx.lineWidth = 1 / scale;
          for (let gx = Math.floor(gMinX); gx <= Math.ceil(gMaxX); gx++) { this.ctx.beginPath(); this.ctx.moveTo(gx, gMinY); this.ctx.lineTo(gx, gMaxY); this.ctx.stroke(); }
          for (let gy = Math.floor(gMinY); gy <= Math.ceil(gMaxY); gy++) { this.ctx.beginPath(); this.ctx.moveTo(gMinX, gy); this.ctx.lineTo(gMaxX, gy); this.ctx.stroke(); }
          if (spi >= 3) {
            this.ctx.strokeStyle = tc.gridMinor; this.ctx.lineWidth = 0.7 / scale;
            for (let gx = Math.floor(gMinX * 12) / 12; gx <= gMaxX; gx += 1 / 12) { if (Math.abs(gx - Math.round(gx)) > 0.01) { this.ctx.beginPath(); this.ctx.moveTo(gx, gMinY); this.ctx.lineTo(gx, gMaxY); this.ctx.stroke(); } }
            for (let gy = Math.floor(gMinY * 12) / 12; gy <= gMaxY; gy += 1 / 12) { if (Math.abs(gy - Math.round(gy)) > 0.01) { this.ctx.beginPath(); this.ctx.moveTo(gMinX, gy); this.ctx.lineTo(gMaxX, gy); this.ctx.stroke(); } }
          }
          this.ctx.restore();
        }
      }
    }

    // Draw preview wall (dashed line from last placed point to cursor)
    if (this.penDrawMode === 'wallSegment' && this.activeTool === 'pen' && this.mode === 'draw' && this.currentWall && this.hasPlacedFirstPoint && this.mouseWorld) {
      let previewEnd = { x: this.mouseWorld.x, y: this.mouseWorld.y };
      if (this.shiftHeld || this._shiftToggle) previewEnd = this.constrainToAxis(this.currentWall.start, previewEnd);
      this.ctx.strokeStyle = '#006BCC';
      this.ctx.setLineDash([5 / scale, 5 / scale]);
      const previewIsLine = this.currentWallSegmentGroup && this.currentWallSegmentGroup.type === 'line';
      if (previewIsLine) {
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.setLineDash([4 / scale, 4.5 / scale]);
        const prevAlpha = this.ctx.globalAlpha;
        this.ctx.globalAlpha = 0.6;
        this.ctx.beginPath();
        this.ctx.moveTo(this.currentWall.start.x, this.currentWall.start.y);
        this.ctx.lineTo(previewEnd.x, previewEnd.y);
        this.ctx.stroke();
        this.ctx.globalAlpha = prevAlpha;
      } else if (this.hollowWalls) {
        const halfT = this.wallSegmentThickness / 2;
        const dx = previewEnd.x - this.currentWall.start.x, dy = previewEnd.y - this.currentWall.start.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = -dy / len * halfT, ny = dx / len * halfT;
        this.ctx.lineWidth = 1.5 / scale;
        this.ctx.beginPath();
        this.ctx.moveTo(this.currentWall.start.x + nx, this.currentWall.start.y + ny);
        this.ctx.lineTo(previewEnd.x + nx, previewEnd.y + ny);
        this.ctx.stroke();
        this.ctx.beginPath();
        this.ctx.moveTo(this.currentWall.start.x - nx, this.currentWall.start.y - ny);
        this.ctx.lineTo(previewEnd.x - nx, previewEnd.y - ny);
        this.ctx.stroke();
      } else {
        this.ctx.lineWidth = wallLW;
        this.ctx.beginPath();
        this.ctx.moveTo(this.currentWall.start.x, this.currentWall.start.y);
        this.ctx.lineTo(previewEnd.x, previewEnd.y);
        this.ctx.stroke();
      }
      this.ctx.setLineDash([]);

      // Draw measurement label on preview
      if (this.showMeasurements) {
        const dx = previewEnd.x - this.currentWall.start.x;
        const dy = previewEnd.y - this.currentWall.start.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0.1) {
          const midX = (this.currentWall.start.x + previewEnd.x) / 2;
          const midY = (this.currentWall.start.y + previewEnd.y) / 2;
          const perpX = -dy / len, perpY = dx / len;
          const labelOffset = 14 / scale;
          const fontSize = Math.max(10, Math.min(13, 13 / this.zoomLevel)) / scale;
          this.ctx.font = `600 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
          this.ctx.fillStyle = '#006BCC';
          this.ctx.textAlign = 'center'; this.ctx.textBaseline = 'middle';
          this.ctx.fillText(this.formatMeasurement(len), midX + perpX * labelOffset, midY + perpY * labelOffset);
          this.ctx.textAlign = 'start'; this.ctx.textBaseline = 'alphabetic';
        }
      }
    }


    // CUSTOM_ROOMDESIGNER_BY_CLAUDE — walls-only stops here: labels were already
    // placed on the first pass and drawing them twice would embolden them.
    if (wallsOnly) return;

    // Draw wall measurement labels for committed segment groups (per-face sub-segments)
    if (this.showMeasurements) {
      const tc = this._tc || this._themeColors();
      // Build sub-segments between consecutive breaks on one face
      const buildSubSegs = (breaks, wallLen) => {
        const result = [];
        for (let i = 0; i < breaks.length - 1; i++) {
          const raw = (breaks[i + 1].param - breaks[i].param) * wallLen;
          const length = raw - breaks[i].adjust - breaks[i + 1].adjust;
          if (length < 0.05) continue;
          result.push({ startParam: breaks[i].param, endParam: breaks[i + 1].param, length, midParam: (breaks[i].param + breaks[i + 1].param) / 2 });
        }
        return result;
      };
      for (const group of this.wallSegmentGroups) {
        const _onCustomizeTab2 = window.roomDesignerOverlay && window.roomDesignerOverlay.currentStep === 'finishing';
        if (group.type === 'line' && !this.showSeamLines && (this._floorMode || this._equipmentMode || _onCustomizeTab2 || this._viewOnlyHideZoneDividers)) continue;
        if (group.type === 'line' && this._viewOnly) continue;
        // CUSTOM_ROOMDESIGNER_BY_CLAUDE — a curve reads as ONE wall, so it gets ONE
        // label: its ARC length (the material you'd actually buy), riding the apex.
        // Without this every facet emitted its own dimension and a curved divider
        // came out as a string of 0' 9" pills down its whole length. Room walls have
        // had this since curves shipped, via the synthetic chord wall built in
        // _getRoomGeoCache — the divider path was simply never given the same.
        const _CV = window.RoomDesignerCurve;
        const _seenCurves = new Set();
        for (const seg of group.segments) {
          if (seg.curveId && _CV) {
            if (_seenCurves.has(seg.curveId)) continue;
            _seenCurves.add(seg.curveId);
            const facets = group.segments
              .filter(f => f.curveId === seg.curveId)
              .sort((p, q) => (p.curveSeq || 0) - (q.curveSeq || 0));
            const arc = facets[0] && facets[0].curveArc;
            if (!arc) continue;
            const a = facets[0].start, b = facets[facets.length - 1].end;
            const arcLen = _CV.arcLength(arc);
            if (!(arcLen > 0.1)) continue;
            const apex = _CV.midHandlePoint(a, b, arc.sag);
            // Sit the label on the OUTSIDE of the bow, which is the direction the
            // apex is already displaced from the chord — inside it would float in
            // open space rather than hug the wall.
            const cmx = (a.x + b.x) / 2, cmy = (a.y + b.y) / 2;
            let ox = apex.x - cmx, oy = apex.y - cmy;
            const ol = Math.hypot(ox, oy);
            if (ol > 1e-6) { ox /= ol; oy /= ol; }
            else { const cl = Math.hypot(b.x - a.x, b.y - a.y) || 1; ox = -(b.y - a.y) / cl; oy = (b.x - a.x) / cl; }
            const cFont = (this.largeUI ? 14.5 : 12) / scale;
            const cLabel = this.formatMeasurement(arcLen);
            this.ctx.font = `600 ${cFont}px Arial`;
            const chw = this.ctx.measureText(cLabel).width / 2, chh = cFont / 2;
            const cppx = 1 / scale, cppy = 2 / scale;
            const coff = (chw + cppx) * Math.abs(ox) + (chh + cppy) * Math.abs(oy) + 6 / scale + this.wallSegmentThickness;
            this._pendingWallLabels.push({
              x: apex.x + ox * coff, y: apex.y + oy * coff, text: cLabel, fontSize: cFont,
              wallLength: arcLen, halfW: chw + cppx, halfH: chh + cppy,
              color: tc.wallColor, sourceWall: facets[0]
            });
            continue;
          }
          const sdx = seg.end.x - seg.start.x, sdy = seg.end.y - seg.start.y;
          const wallLen = Math.sqrt(sdx * sdx + sdy * sdy);
          if (wallLen < 0.1) continue;
          const nx = -sdy / wallLen, ny = sdx / wallLen;
          const fontSize = (this.largeUI ? 14.5 : 12) / scale;
          // Emit label at a sub-segment midpoint on the given face
          const emitLabel = (ss, faceSign) => {
            const px = seg.start.x + ss.midParam * sdx;
            const py = seg.start.y + ss.midParam * sdy;
            const label = this.formatMeasurement(ss.length);
            this.ctx.font = `600 ${fontSize}px Arial`;
            const hw = this.ctx.measureText(label).width / 2;
            const hh = fontSize / 2;
            const ppx = 1 / scale, ppy = 2 / scale;
            const off = (hw + ppx) * Math.abs(nx) + (hh + ppy) * Math.abs(ny) + 6 / scale + this.wallSegmentThickness;
            this._pendingWallLabels.push({ x: px + faceSign * nx * off, y: py + faceSign * ny * off, text: label, fontSize, wallLength: ss.length, halfW: hw + ppx, halfH: hh + ppy, color: tc.wallColor, sourceWall: seg });
          };
          if (this._getWallBreakPoints) {
            const allBPs = this._getWallBreakPoints(seg);
            // Separate breaks by face
            const posBreaks = allBPs.filter(bp => bp.sides === 'pos' || bp.sides === 'both');
            const negBreaks = allBPs.filter(bp => bp.sides === 'neg' || bp.sides === 'both');
            const posSegs = buildSubSegs(posBreaks, wallLen);
            const negSegs = buildSubSegs(negBreaks, wallLen);
            // Check if both faces produce matching measurement lists
            const match = posSegs.length === negSegs.length &&
              posSegs.every((ps, i) => Math.abs(ps.length - negSegs[i].length) < 1 / 120);
            if (match) {
              // Faces match — emit one set, flip away from any one-sided T-junction
              let labelSign = 1;
              let posBlocked = false, negBlocked = false;
              for (const bp of allBPs) {
                if (bp.param < 0.02 || bp.param > 0.98) continue;
                if (bp.sides === 'pos' || bp.sides === 'both') posBlocked = true;
                if (bp.sides === 'neg' || bp.sides === 'both') negBlocked = true;
              }
              if (posBlocked && !negBlocked) labelSign = -1;
              for (const ss of posSegs) emitLabel(ss, labelSign);
            } else {
              // Faces differ — emit both sets on their respective sides
              for (const ss of posSegs) emitLabel(ss, 1);
              for (const ss of negSegs) emitLabel(ss, -1);
            }
          } else {
            // Fallback: no break points, single label
            const label = this.formatMeasurement(wallLen);
            this.ctx.font = `600 ${fontSize}px Arial`;
            const hw = this.ctx.measureText(label).width / 2;
            const hh = fontSize / 2;
            const ppx = 1 / scale, ppy = 2 / scale;
            const off = (hw + ppx) * Math.abs(nx) + (hh + ppy) * Math.abs(ny) + 6 / scale + this.wallSegmentThickness;
            const mx = (seg.start.x + seg.end.x) / 2, my = (seg.start.y + seg.end.y) / 2;
            this._pendingWallLabels.push({ x: mx + nx * off, y: my + ny * off, text: label, fontSize, wallLength: wallLen, halfW: hw + ppx, halfH: hh + ppy, color: tc.wallColor, sourceWall: seg });
          }
        }
      }
    }
  }
}

window.RoomDesignerWallSeg = RoomDesignerWallSeg;

